import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks } from '@fortawesome/free-solid-svg-icons'
import { LinearProgress } from '@material-ui/core';
import CommonContext, { ApiRoutes, AppNavPaths } from "../Common";
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    IconCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    VariableLinkCellRenderer
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import { onFieldChange, PageHeading, PageWrap, SmallOutlineButton } from '../common/forms/FormElements';
import { Can } from '../Can';
import authService from '../api-authorization/AuthorizeService';
import { getUserProfile } from '../common/UserProfile';

export default class QuestionsSearch extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            isExporting: false
        };

        this.onRowSelected = this.onRowSelected.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount = () => {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount = async () => {
        await authService.unsubscribe(this._subscription);
    }

    onChange = onFieldChange;

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();

        if (!!isAuthenticated) {
            const { userPermissions } = await getUserProfile();

            const gridOptions = createGridOptions(this);

            const canEdit = !!userPermissions.includes("question.edit");

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                iconRenderer: IconCellRenderer,
                variableLinkRenderer: VariableLinkCellRenderer
            };
            gridOptions.onRowSelected = this.onRowSelected;
            gridOptions.columnDefs = this.getColumnDefs(canEdit);
            gridOptions.useLoading = true;
            gridOptions.loadingTemplate = '<span className="ag-overlay-loading-center "><i class="faSaveSpinner fa fa-md fa-circle-notch"></i> Loading ...</span>';

            const dataSource = createDataSource(ApiRoutes.questions.search(), gridOptions);

            this.setState({
                loading: false,
                gridOptions: gridOptions,
                dataSource: dataSource
            });
        }
    }

    getColumnDefs(canEdit) {

        //https://www.ag-grid.com/documentation/javascript/column-definitions/

        const defs = [
            {
                headerName: "",
                valueGetter: "node.id",
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                minWidth: 35,
                cellRenderer: this.indexCellRenderer
            },
            (canEdit ? {
                colId: 'Id',
                minWidth: 75,
                maxWidth: 75,
                headerName: '',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellStyle: { color: "rgba(0,0,0,0)" },
                cellRendererParams: {
                    clicked: (id) => { this.props.history.push(`${AppNavPaths.Questions}/${id}`) },
                    idField: 'id',
                    iconClass: 'fa-edit'
                }
            } : {
                colId: 'Id',
                minWidth: 75,
                maxWidth: 75,
                headerName: '',
                sortable: false
            }),
            {
                colId: 'Name',
                headerName: 'Question Name',
                field: 'name',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                }
            },
            {
                colId: 'Title',
                headerName: 'Question Text',
                field: 'title',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                }
            },
            {
                colId: 'SubText',
                headerName: 'Question Sub Text',
                field: 'subText',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                }
            },
            {
                colId: 'UnitOfMeasurement.Name',
                headerName: 'Unit Of Measurement',
                field: 'unitOfMeasurementText',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                }
            },
            {
                colId: 'InputType.Name',
                headerName: 'Input Type',
                field: 'inputTypeText',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                }
            }];

        return defs;
    }

    //https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    //Possibly use lodash dequeue?
    onRowSelected(e) {

        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs
        });
    }

    render() {
        const {
            rowData,
            gridOptions
        } = this.state;

        return (
            <CommonContext.Consumer>
                {
                    value => {
                        if (this.state.loading) return (<LinearProgress variant="indeterminate" color="secondary" />);

                        return (
                            <PageWrap>
                                <PageHeading>
                                    <FontAwesomeIcon icon={faTasks} className="mr-2 text-muted" />
                                    <span>Questions</span>
                                    <Can do="create" on="question">
                                        <SmallOutlineButton onClick={() => { this.props.history.push(AppNavPaths.QuestionsNew)}} className="float-right">
                                            <i className="fa fa-plus-square fa-lg" />
                                            <span className="ml-2 small-viewport-hide">
                                                Create Question
                                            </span>
                                        </SmallOutlineButton>
                                    </Can>
                                </PageHeading>
                                <DataGridToolbar
                                    entity="Question"
                                    gridApi={this.state.gridApi}
                                    dataSource={this.state.dataSource}
                                    hideAdd={true}
                                    hideExcelButton={true}
                                    gridOptions={this.state.gridOptions}
                                    serverExport={{ apiPath: ApiRoutes.questions.excelExport(), filePrefix: 'QuestionOptionGroupOptions' }}
                                />
                                <DataGrid
                                    domLayout={"normal"}
                                    rowData={rowData}
                                    gridOptions={gridOptions}
                                    gridStatus={this.state.gridStatus}
                                />
                            </PageWrap>
                        )
                    }
                }
            </CommonContext.Consumer>
        )
    }
}